import { createStore, StoreCore } from "@priolo/jon";
import cyclesApi from "api/cycles";
import { ExportCSVData } from "components/controls/ExportDialog";
import dayjs from "dayjs";
import draftSo from "stores/draft";
import farmSo from "stores/farm";
import guSo from "stores/growUnit";
import { LastDateRange } from "stores/planner/utils/fetch";
import { Cycle } from "types/Cycle";
import { DateNumber, Uuid } from "types/global";
import { buildDateRange, dateRangeToUrl, haveValueDateRange, normalizeDateRange } from "utils";
import { getRef } from "utils/humanize";
import { getCycleStatus, getCycleStatusLabel, getLayersInHarvest } from "./utils/cycle";
import { getCycleFilteredUrl } from "./utils/fetch";
import { FetchPayload } from "./types";



const setup = {

	state: {
		/** tutti i CYCLES */
		all: <Cycle[]>[],
		/** il CYCLE selezionato in questo momento */
		select: <Cycle>null,
		isCsvExportOpen: false,
	},

	getters: {
		/** Recupero da "all" il cycle con l'uuid specificato */
		getCycleByUuid: (uuid: Uuid, store?: CycleStore): Cycle => !uuid ? null : store.state.all
			?.find(cycle => cycle.cycleUuid == uuid),
		getById: (id: number, store?: CycleStore): Cycle => !id ? null : store.state.all
			?.find(cycle => cycle.id == id),
		getIndexByUuid: (cycleUuid: Uuid, store?: CycleStore): number => store.state.all
			?.findIndex(c => c.cycleUuid == cycleUuid),
		getByLotCode: (lotCode: string, store?: CycleStore): Cycle => store.state.all
			.find(c => c.cropLots.some(cl => cl.lotCode == lotCode)),

		/** per l'export in CSV */
		getExportData: (_: void, store?: CycleStore): ExportCSVData => {
			const cycles = getCycleFilteredUrl(store.state.all)
			const items = cycles.flatMap(cycle => cycle.cropLots.map(cropLot => [
				// CROP-LOT
				cropLot?.cropName ?? "",
				cropLot?.lotCode ?? "",
				cropLot?.trolleysToHarvest?.toFixed(0) ?? "",

				cropLot?.unitsYield?.toFixed(0) ?? "",
				cropLot?.plannedYield?.toFixed(2) ?? "",
				cropLot?.yield?.toFixed(2) ?? "",

				cropLot?.seedUsedG?.toFixed(0) ?? "",
				cropLot?.gelUsedG?.toFixed(0) ?? "",
				// CyCLE
				getRef(cycle?.cycleUuid),
				farmSo.getGrowUnit(cycle?.growUnitId).name,
				getCycleStatusLabel(getCycleStatus(cycle)),
				getLayersInHarvest(cycle).toString(),

				dayjs(cycle.seedingDate).format("YYYY-MM-DD"),
				dayjs(cycle.harvestingDate).format("YYYY-MM-DD"),

				cycle.unitsYield?.toFixed(0) ?? "",
				cycle.plannedYield?.toFixed(2) ?? "",
				cycle.yield?.toFixed(2) ?? "",
			]))
			const farmName = farmSo.state.select.name
			const range = dateRangeToUrl(LastDateRange)
			return {
				headers: [
					"CROP NAME", "CROP LOT CODE", "CROP TROLLEYS TO HARVEST",
					"CROP UNITS YIELD", "CROP PLANNED YIELD", "CROP YIELD",
					"CROP SEED USED", "CROP GEL USED",
					"CYCLE REF", "CYCLE GROW UNIT", "CYCLE STATUS", "CYCLE LAYERS",
					"CYCLE SEEDING DATE", "CYCLE HARVESTING DATE",
					"CYCLE UNITS YIELD", "CYCLE PLANNED YIELD", "CYCLE YIELD",
				],
				items,
				fileName: `CROP-LOTS ${farmName} (${range})`
			}
		},
	},

	actions: {
		// [II] ottimizzare con store TASKS
		/** Carico tutti i CYCLEs di una specifica FARM */
		fetch: async (payload: FetchPayload, store?: CycleStore) => {
			// controllo ci siano tutti i dati
			const farmId = payload?.farmId ?? guSo.state.lastFarmId
			const dateRange = normalizeDateRange(payload?.dateRange, LastDateRange)
			if (!farmId || !haveValueDateRange(dateRange)) return

			// a sto punto carico di brutto...
			const { data: cyclesLoad } = await cyclesApi.indexByFarm(farmId, dateRange)
			// se devo aggiungere...
			if (payload.add) {
				draftSo.addCyclesInSessions(cyclesLoad)
				// ...altrimenti sostituisco tutto
			} else {
				draftSo.setCyclesInSessions(cyclesLoad)
			}

			return cyclesLoad
		},
		fetchByGrowUnitSingleDay: async (
			{ growUnitId, date }: { growUnitId: number, date: DateNumber },
			store?: CycleStore
		) => {
			const range = buildDateRange(date)
			const { data } = await cyclesApi.indexByGrowUnit(growUnitId, range)
			store.setAll(data)
		},
	},

	mutators: {
		setAll: (all: Cycle[]) => ({ all }),
		setSelect: (select: Cycle) => ({ select }),
		setIsCsvExportOpen: (isCsvExportOpen: boolean) => ({ isCsvExportOpen }),
	},
}

export type CycleState = typeof setup.state
export type CycleGetters = typeof setup.getters
export type CycleActions = typeof setup.actions
export type CycleMutators = typeof setup.mutators
export interface CycleStore extends StoreCore<CycleState>, CycleGetters, CycleActions, CycleMutators {
	state: CycleState
}
const cycleSo = createStore(setup) as CycleStore
export default cycleSo